// =============================================================================
// Claude Powered Analytics — landing / runtime (/analytics) — newspaper theme
// =============================================================================

import { stateful, useState, useEffect } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { runAnalyticsSkill, getAnalyticsRunStatus, listSkills, exportDocument } from '../../db/analytics-resolvers.js';
import { newspaperStyle, masthead, NP } from './newspaper.js';
import { chartToSvg } from '../../charts.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

function b64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
async function downloadReport(markdown: string, title: string, format: 'pdf' | 'docx' | 'xlsx') {
  try {
    const res = JSON.parse(await exportDocument(JSON.stringify({ markdown, title, author: 'Claude Powered Analytics' }), format)());
    const url = URL.createObjectURL(b64ToBlob(res.base64, res.mime));
    const a = document.createElement('a');
    a.href = url;
    a.download = res.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    /* ignore */
  }
}

interface SkillBtn {
  id: string;
  title: string;
}

function renderInline(text: string) {
  const parts: unknown[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(html`<strong>${tok.slice(2, -2)}</strong>`);
    else if (tok.startsWith('`')) parts.push(html`<code class="px-1 py-0.5 rounded bg-base-300 text-xs">${tok.slice(1, -1)}</code>`);
    else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm && /^https?:\/\//i.test(lm[2])) parts.push(html`<a href=${lm[2]} target="_blank" rel="noopener noreferrer">${lm[1]}</a>`);
      else parts.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
const splitCells = (line: string) => {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
};
const isSeparatorRow = (cells: string[]) => cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c.replace(/\s+/g, '')));

function renderMarkdown(md: string) {
  const blocks: unknown[] = [];
  let bullets: unknown[] = [];
  let tableRows: string[] = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(html`<ul class="list-disc ml-5 my-1 space-y-1">${bullets}</ul>`);
      bullets = [];
    }
  };
  const flushTable = () => {
    if (!tableRows.length) return;
    const rows = tableRows.map(splitCells);
    tableRows = [];
    const header = rows[0];
    const bodyStart = rows[1] && isSeparatorRow(rows[1]) ? 2 : 1;
    const body = rows.slice(bodyStart);
    blocks.push(html`
      <table class="np-table">
        <thead>
          <tr>${header.map((c) => html`<th>${renderInline(c)}</th>`)}</tr>
        </thead>
        <tbody>
          ${body.map((r) => html`<tr>${r.map((c) => html`<td>${renderInline(c)}</td>`)}</tr>`)}
        </tbody>
      </table>`);
  };
  let inChart = false;
  let chartLines: string[] = [];
  for (const raw of md.split('\n')) {
    const t = raw.replace(/\r$/, '').trim();
    if (inChart) {
      if (t === '```') {
        try {
          blocks.push(html`<div class="np-chart">${unsafeHTML(chartToSvg(JSON.parse(chartLines.join('\n'))))}</div>`);
        } catch {
          /* invalid chart json */
        }
        inChart = false;
        chartLines = [];
      } else {
        chartLines.push(raw);
      }
      continue;
    }
    if (t.startsWith('```')) {
      if (/^```chart\b/.test(t)) { flush(); flushTable(); inChart = true; chartLines = []; }
      continue;
    }
    if (t.startsWith('|') && t.length > 1) { flush(); tableRows.push(t); continue; }
    flushTable();
    if (t === '') { flush(); continue; }
    if (t.startsWith('### ')) { flush(); blocks.push(html`<h4 class="font-semibold text-sm mt-3 mb-1">${renderInline(t.slice(4))}</h4>`); continue; }
    if (t.startsWith('## ')) { flush(); blocks.push(html`<h3 class="font-semibold text-base mt-3 mb-1">${renderInline(t.slice(3))}</h3>`); continue; }
    if (t.startsWith('# ')) { flush(); blocks.push(html`<h3 class="font-semibold text-base mt-3 mb-1">${renderInline(t.slice(2))}</h3>`); continue; }
    const bm = t.match(/^[-*]\s+(.*)$/);
    if (bm) { bullets.push(html`<li>${renderInline(bm[1])}</li>`); continue; }
    flush();
    blocks.push(html`<p class="my-1">${renderInline(t)}</p>`);
  }
  flush();
  flushTable();
  return blocks;
}

export const AnalyticsPage = stateful(() => {
  const [skills, setSkills] = useState<SkillBtn[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [question, setQuestion] = useState<string>('');
  const [report, setReport] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [steps, setSteps] = useState<string[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        setSkills(JSON.parse(await listSkills()()));
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const invoke = async (skillId: string, q: string) => {
    setActive(skillId);
    setLoading(true);
    setReport('');
    setSteps([]);
    try {
      const runId: string = await runAnalyticsSkill(skillId, q)();
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 1200));
        const raw: string = await getAnalyticsRunStatus(runId)();
        let st: any;
        try {
          st = JSON.parse(raw);
        } catch {
          continue;
        }
        setSteps(Array.isArray(st.steps) ? st.steps : []);
        if (st.done) {
          if (st.error) setReport('⚠ ' + st.error);
          else setReport(st.report || '(empty response)');
          break;
        }
      }
    } catch (e: any) {
      setReport('⚠ ' + String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  };

  const ask = () => {
    const q = question.trim();
    if (!q || loading) return;
    invoke('ask-anything', q);
  };

  return html`
    ${newspaperStyle()}
    <div class="np-wrap">
      <div class="container mx-auto px-4 py-6 max-w-6xl">
        ${masthead('Claude Powered Analytics', 'Live agentic research desk')}

        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          ${skills.map(
            (s) => html`
              <button class="btn np-btn h-auto py-3 justify-center" ?disabled=${loading} @click=${() => invoke(s.id, '')}>
                <span class="text-sm tracking-wide">${loading && active === s.id ? 'Running…' : s.title}</span>
              </button>
            `,
          )}
        </div>

        <div class="np-card rounded-lg p-4 mb-6">
          <div class="text-xs uppercase mb-2" style="letter-spacing:.2em; color:#6b5c44;">Ask the desk</div>
          <div class="flex gap-2">
            <input
              class="input input-bordered flex-1 bg-transparent"
              style="font-family: Georgia, serif;"
              placeholder="e.g. Which machine has the most complaints this week, and why?"
              .value=${question}
              ?disabled=${loading}
              @input=${(e: Event) => setQuestion((e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') ask();
              }}
            />
            <button class="btn np-btn" ?disabled=${loading} @click=${ask}>${loading && active === 'ask-anything' ? 'Asking…' : 'Ask'}</button>
          </div>
        </div>

        ${loading
          ? html`
              <div class="np-card rounded-lg p-5">
                <div class="text-xs uppercase mb-3" style="letter-spacing:.2em; color:#6b5c44;">On the wire…</div>
                <ul class="text-sm space-y-1.5" style="font-family: Georgia, serif;">
                  ${steps.map(
                    (s, i) => html`<li class="flex items-center gap-2">
                      <span style="color:#6b5c44;">${i === steps.length - 1 ? '…' : '—'}</span><span>${s}</span>
                    </li>`,
                  )}
                </ul>
              </div>
            `
          : report
            ? html`
                <div class="np-card rounded-lg p-6">
                  <div class="np-divider mb-2"></div>
                  <h2 class="text-center text-3xl font-bold uppercase" style="font-family:'Playfair Display',Georgia,serif;">Report</h2>
                  <div class="text-center text-xs uppercase mb-2" style="letter-spacing:.22em; color:#6b5c44;">${active ?? ''}</div>
                  <div class="np-divider mb-3"></div>
                  <div class="flex justify-center items-center gap-2 mb-4 text-xs" style="color:#6b5c44;">
                    <span class="uppercase" style="letter-spacing:.15em;">Export</span>
                    <button class="btn np-btn btn-xs" @click=${() => downloadReport(report, active ?? 'report', 'pdf')}>PDF</button>
                    <button class="btn np-btn btn-xs" @click=${() => downloadReport(report, active ?? 'report', 'docx')}>Word</button>
                    <button class="btn np-btn btn-xs" @click=${() => downloadReport(report, active ?? 'report', 'xlsx')}>Excel</button>
                  </div>
                  <div class="np-report">${renderMarkdown(report)}</div>
                </div>
              `
            : html`
                <div class="flex flex-col items-center text-center py-8 opacity-80">
                  <img src=${NP.ENGRAVING} alt="" style="height:170px; mix-blend-mode:multiply;" />
                  <p class="mt-3" style="font-family:'Old Standard TT',Georgia,serif; color:#6b5c44;">Pick a column above, or ask the desk.</p>
                </div>
              `}
        ${nothing}
      </div>
    </div>
  `;
});
